// QR Kiosk v2 — KV2-7 task DETAIL (/public-v2/tasks/:number). Deep-link-safe fetch
// by number (E2), route-scoped, §5 4-branch tree. Header card + status-gated meta
// grid + instructions/description + done block. Actions render STRICTLY from the
// server's allowedActions (never kioskStatus alone) — composer + transition strip +
// view-only line all delegate to that gate (§9). Tokens only; no MUI/dt/hex.
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Roles } from "../../../account/models/AccountModels";
import { getKioskTask } from "./kioskTasksSlice";
import { KioskTaskDetail } from "./kioskTasksModels";
import { canAddNote, isViewOnly, TASK_DUE_FORMAT } from "./taskUtils";
import { KPage } from "../shared/KPage";
import { GD } from "../shared/glyphs";
import { EmptyCard } from "../shared/EmptyCard";
import { ErrorCallout } from "../shared/ErrorCallout";
import { CachedErrorState } from "../shared/CachedErrorState";
import { useSurfaceState } from "../shared/useSurfaceState";
import { formatClockTime } from "../shared/clock";
import { KDate, MetaCell } from "../reports/ReportBits";
import { REPORT_DATE_FORMAT } from "../reports/reportUtils";
import { TaskStatusChip, TaskFileChipGrid } from "./TaskBits";
import { TaskDetailSkeleton } from "./TaskSkeletons";
import { TaskNoteComposer } from "./TaskNoteComposer";
import { TaskNotesTimeline } from "./TaskNotesTimeline";
import { TaskActionStrip } from "./TaskActionSheets";

const sectionBorderTop = "1px solid var(--border-soft)";

function HeaderCard({ tk }: { tk: KioskTaskDetail }) {
  const { translate } = useTranslation("public");
  const done = tk.kioskStatus === "done";
  const closingFiles = (tk.files ?? []).filter((f) => f.uploadContext === "ClosingRemarks");
  const border = tk.isOverdue ? "color-mix(in oklab, var(--red) 32%, var(--border))" : "var(--border)";

  return (
    <div
      className="kv2-enter"
      style={{
        margin: "14px 14px 0", background: "var(--card)", border: `1px solid ${border}`,
        borderRadius: 12, boxShadow: "var(--shadow)", overflow: "hidden", flexShrink: 0,
      }}
    >
      {/* Header */}
      <div style={{ padding: "14px 16px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600, color: "var(--tier-accent)" }}>
            #{tk.number}
          </span>
          <span style={{ flex: 1 }} />
          <TaskStatusChip kioskStatus={tk.kioskStatus} isOverdue={tk.isOverdue} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em", color: "var(--ink)", marginTop: 7, lineHeight: 1.3 }}>
          {tk.title}
        </div>
      </div>

      {/* Meta grid — cells render conditionally per the designer; em-dash for absent. */}
      <div
        style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 14,
          padding: "2px 16px 6px", borderTop: sectionBorderTop,
        }}
      >
        <MetaCell label={translate("kioskV2TaskDue")} value={<KDate date={tk.dueDate} format={TASK_DUE_FORMAT} />} />
        <MetaCell label={translate("kioskV2TaskAssignee")} value={tk.assigneeName} mono={false} />
        <MetaCell label={translate("kioskV2TaskMachine")} value={tk.machineSerialNumber ? `S/N ${tk.machineSerialNumber}` : undefined} />
        {tk.actualStartDate && <MetaCell label={translate("kioskV2TaskStarted")} value={<KDate date={tk.actualStartDate} format={REPORT_DATE_FORMAT} />} />}
        {tk.kioskStatus === "on_hold" && tk.holdReason && <MetaCell label={translate("kioskV2TaskOnHold")} value={tk.holdReason} mono={false} />}
        {tk.actualEndDate && <MetaCell label={translate("kioskV2TaskClosed")} value={<KDate date={tk.actualEndDate} format={REPORT_DATE_FORMAT} />} />}
        {tk.pendingReview && <MetaCell label={translate("kioskV2TaskReview")} value={translate("kioskV2TaskAwaitingReview")} mono={false} />}
      </div>

      {/* Instructions (only when truthy) */}
      {tk.workInstructions && (
        <div style={{ padding: "10px 16px 14px", borderTop: sectionBorderTop }}>
          <div className="eyebrow" style={{ color: "var(--faint)", marginBottom: 5 }}>{translate("kioskV2TaskInstructions")}</div>
          <div style={{ fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.55 }}>{tk.workInstructions}</div>
        </div>
      )}

      {/* Description (only when truthy) */}
      {tk.description && (
        <div style={{ padding: "10px 16px 14px", borderTop: sectionBorderTop }}>
          <div className="eyebrow" style={{ color: "var(--faint)", marginBottom: 5 }}>{translate("kioskV2TaskDescription")}</div>
          <div style={{ fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.55 }}>{tk.description}</div>
        </div>
      )}

      {/* Done block (green-tinted; ClosingRemarks files only) */}
      {done && (
        <div style={{ padding: "12px 16px 14px", borderTop: "1px solid var(--border)", background: "color-mix(in oklab, var(--green) 5%, var(--card))" }}>
          <div className="eyebrow" style={{ color: "color-mix(in oklab, var(--green) 70%, var(--ink))", marginBottom: 5 }}>
            {translate("kioskV2TaskCompletionNote")}
          </div>
          <div style={{ fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.55 }}>{tk.completionNotes}</div>
          {closingFiles.length > 0 && <TaskFileChipGrid files={closingFiles} />}
        </div>
      )}
    </div>
  );
}

export function TaskDetailV2() {
  const { number } = useParams<{ number: string }>();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const task = useAppSelector((s) => s.kioskTasks.task);
  const taskErrorStatus = useAppSelector((s) => s.kioskTasks.taskErrorStatus);
  const role = useAppSelector((s) => s.account.user?.role);
  const isViewer = role === Roles.VIEWER;
  // §7.1-A back target = the tasks list. The task DTO carries no internalNumber, so read
  // the machine from the store; KPage's own store fallback is NOT usable here (it resolves
  // to the MENU, not the tasks list) — pass an explicit backTo + backNested instead.
  const machineFromStore = useAppSelector((s) => s.public.machine?.internalNumber);
  const tasksBackTo = machineFromStore ? `/public-v2/machines/${machineFromStore}/tasks` : undefined;

  useEffect(() => {
    if (isViewer) return; // client viewer gate — no fetch (§9.1)
    if (number) dispatch(getKioskTask({ number }));
  }, [number, isViewer, dispatch]);

  // Route-scoped task — stale detail must never flash.
  const tk = task && number && task.number.toLowerCase() === number.toLowerCase() ? task : undefined;
  const st = useSurfaceState("task", number);

  // Viewer client gate — denied card, no network call (§9.1 / §8.2).
  if (isViewer) {
    return (
      <KPage backNested backTo={tasksBackTo}>
        <EmptyCard glyph={GD.lock} title={translate("kioskV2TaskNoAccessTitle")} hint={translate("kioskV2TaskNoAccessHint")} />
      </KPage>
    );
  }

  let body: React.ReactNode = null;
  if (!tk && st.failed) {
    // §9.7 deep-link failure branches — server folds cross-tenant/cancelled/bad-number into 404.
    if (taskErrorStatus === 404) {
      body = <EmptyCard glyph={GD.task} title={translate("kioskV2TaskNotFoundTitle")} hint={translate("kioskV2TaskNotFoundHint")} />;
    } else if (taskErrorStatus === 403) {
      body = <EmptyCard glyph={GD.lock} title={translate("kioskV2TaskNoAccessTitle")} hint={translate("kioskV2TaskNoAccessHint")} />;
    } else {
      body = (
        <ErrorCallout
          title={translate("kioskV2TaskLoadFailedTitle")}
          hint={translate("kioskV2DashboardLoadFailedHint")}
          onRetry={() => { if (number) dispatch(getKioskTask({ number })); }}
          retryLabel={translate("kioskV2Retry")}
        />
      );
    }
  } else if (!tk) {
    body = <TaskDetailSkeleton />;
  } else {
    const content = (
      <>
        <HeaderCard tk={tk} />
        {isViewOnly(tk) && (
          <div style={{ margin: "10px 14px 0", fontSize: 12, color: "var(--muted)", lineHeight: 1.5, flexShrink: 0 }}>
            {translate("kioskV2TaskViewOnly")}
          </div>
        )}
        {number && canAddNote(tk) && <TaskNoteComposer tk={tk} number={number} />}
        {/* Note timeline sits BELOW the composer so a freshly added note lands right under
            the "add note" affordance. Renders for every kiosk-visible task (view-only + done
            included) — independent of allowedActions; the component self-hides when empty. */}
        <TaskNotesTimeline tk={tk} />
        {number && <TaskActionStrip tk={tk} number={number} />}
      </>
    );
    // Cached detail + refetch failed → dim card AND actions (acting on stale state forbidden).
    body = st.failed ? (
      <CachedErrorState
        title={translate("kioskV2TaskLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={() => { if (number) dispatch(getKioskTask({ number })); }}
        retryLabel={translate("kioskV2Retry")}
        cachedAt={st.loadedAt}
        cachedLabel={st.loadedAt != null ? translate("kioskV2ShowingCachedFrom", { time: formatClockTime(st.loadedAt) }) : undefined}
      >
        {content}
      </CachedErrorState>
    ) : (
      content
    );
  }

  return (
    <KPage backNested backTo={tasksBackTo}>
      {body}
    </KPage>
  );
}

export default TaskDetailV2;
